import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '@/config';
import { ClipLoader } from 'react-spinners';
import { fetchInquiryOptions, type MasterOption } from '@/data';

type InquiryStatus = 'open' | 'in_progress' | 'answered' | 'closed';
type Inquiry = {
  id: number;
  created_at: string;
  category: string;
  subject: string; // ← 表示は「お問い合わせ内容」
  status: InquiryStatus;
  ticket_no?: string;
};

const statusLabel: Record<InquiryStatus, string> = {
  open: '未対応',
  in_progress: '対応中',
  answered: '回答済',
  closed: 'クローズ',
};
const statusChip: Record<InquiryStatus, string> = {
  open: 'bg-red-100 text-red-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  answered: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function SupportHistoryPage() {
  const [items, setItems] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [inquiryOptions, setInquiryOptions] = useState<MasterOption[]>([]);
  const catMap = useMemo(
    () => Object.fromEntries(inquiryOptions.map(o => [String(o.value), o.label])),
    [inquiryOptions]
  );

  const statusFilter = (params.get('status') as InquiryStatus) || 'open';
  const page = Number(params.get('page') || 1);

  useEffect(() => {
    (async () => {
      try {
        const opts = await fetchInquiryOptions();
        setInquiryOptions(Array.isArray(opts) ? opts : []);
      } catch (e) {
        console.error('問い合わせ種別マスター取得に失敗', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/user/login');
          return;
        }
        const q = new URLSearchParams();
        q.set('page', String(page));
        q.set('size', '20');
        q.set('status', statusFilter);
        const res = await fetch(`${API_BASE_URL}/v1/user/contact/list?` + q.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: Inquiry[] = await res.json();
        setItems(Array.isArray(data) ? data : []);
        setErr(null);
      } catch (e) {
        setErr('お問い合わせ履歴の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    })();
  }, [statusFilter, page, navigate]);

  const setFilter = (s: InquiryStatus) => {
    params.set('status', s);
    params.set('page', '1');
    setParams(params, { replace: true });
  };

  const renderCategoryChip = (value?: string) => {
    const v = (value ?? '').trim();
    const label = v ? (catMap[v] ?? v) : '-';
    const isDash = label === '-';
    return (
      <span
        title={label}
        className={`inline-block px-2 py-0.5 rounded-full text-xs border leading-5 whitespace-normal break-words
                    ${isDash ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
        style={{ maxWidth: '20rem' }}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <section className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-sky-400">
        <h1 className="text-xl font-bold text-sky-600 mb-6">お問い合わせ履歴</h1>

        {loading && <div className="py-8 text-center"><ClipLoader loading size={28} /></div>}
        {!loading && err && <div className="bg-red-50 text-red-700 border border-red-200 rounded p-4">{err}</div>}

        {!loading && !err && (
          items.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-base table-fixed">
                <thead>
                  <tr className="bg-gray-100 text-sm text-gray-600">
                    <th className="p-4 text-left w-48 md:w-56">受付日時</th>
                    <th className="p-4 text-left w-56 md:w-72 lg:w-80">カテゴリ</th>
                    <th className="p-4 text-left">お問い合わせ内容</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(row => (
                    <tr key={row.id} className="border-t hover:bg-sky-50">
                      <td className="p-4 align-top">{fmt(row.created_at)}</td>

                      <td className="p-4 w-56 md:w-72 lg:w-80 align-top">
                        <div className="max-w-full">
                          {renderCategoryChip(row.category)}
                        </div>
                      </td>

                      {/* ← 折り返し表示（truncate/min-w-0 を排除） */}
                      <td className="p-4 align-top">
                        <div className="flex items-start gap-2">
                          {row.ticket_no && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded shrink-0">
                              {row.ticket_no}
                            </span>
                          )}
                          <span className="break-words whitespace-normal leading-6">
                            {row.subject}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-10">該当するお問い合わせはありません。</div>
          )
        )}
      </section>
    </div>
  );
}
